import ApiService from '@/core/api/api.service';

import { functionsWithNotifications } from '@/core/notifications';
import workerAssessmentMessages from './worker-assessment.messages';

import { AssessmentSection } from '@/core/assessment';

const functions = {
  get,
  updateWorkerAssessment,
};

export default {
  ...functionsWithNotifications(functions, workerAssessmentMessages),
};

const workerAssessmentApi = new ApiService();

async function all(workerUuid) {
  const response = await workerAssessmentApi.get(`workers/${workerUuid}/worker_assessments`);
  const { data, meta } = response.data;
  return { data, meta };
}

/**
 * Get assessment (workerAssessment (=answers) with assessment(=questions)) for a given worker
 * @param {string} workerUuid worker uuid
 * @param {string} clientUuid Optional. The uuid of the client that wants to fetch the assessment for one of their contractor's workers.
 */
async function get(workerUuid, clientUuid) {
  const workerAssessment = await getWorkerAssessment(workerUuid);
  const workerAssessmentUuid = _.get(workerAssessment, 'relationships.assessment.data.attributes.uuid');
  const assessment = await getAssessment(workerAssessmentUuid, workerUuid, clientUuid);
  const result = _.cloneDeep(workerAssessment);
  if (result.relationships) {
    result.relationships.assessment = {
      data: assessment,
    };
  }
  return result;
}

/**
 * Get assessment (questions)
 * @param {string} assessmentUuid
 * @param {string} workerUuid
 * @param {string} clientUuid Optional. The client context (e.g. if a client is viewing 'my contractors')
 */
async function getAssessment(assessmentUuid, workerUuid, clientUuid) {
  if (!assessmentUuid) {
    return null;
  }

  const params = {
    client_uuid: clientUuid,
    worker_uuid: workerUuid,
  };

  const response = await workerAssessmentApi.get(`assessments/${assessmentUuid}`, { params });
  const {
    attributes, meta, relationships,
  } = response.data.data;
  return {
    attributes,
    meta,
    relationships: {
      sections: relationships.sections.data.map(section => new AssessmentSection(section)),
    },
  };
}

/**
 * Get worker assessment (answers)
 * @param {string} workerUuid
 */
async function getWorkerAssessment(workerUuid) {
  const allResponse = await all(workerUuid);
  const lastAssessment = _.get(allResponse, 'data', []).slice(-1)[0];
  if (!lastAssessment) {
    return {};
  }

  const response = await workerAssessmentApi.get(`worker_assessments/${lastAssessment.uuid}`);
  return _.get(response, 'data.data', {});
}

async function updateWorkerAssessment(workerAssessment = {}, answers) {
  const requestBody = {
    worker_assessment: {
      assessment_uuid: _.get(workerAssessment, 'relationships.assessment.data.attributes.uuid'),
      worker_uuid: _.get(workerAssessment, 'relationships.worker.data.attributes.uuid'),
      metadata: answers,
    },
  };

  const response = await workerAssessmentApi.put(`worker_assessments/${workerAssessment.uuid}`, requestBody);
  const updatedWorkerAssessment = _.get(response, 'data.data');
  const workerAssessmentUuid = _.get(updatedWorkerAssessment, 'relationships.assessment.data.attributes.uuid');
  const assessmentWorkerUuid = _.get(updatedWorkerAssessment, 'relationships.worker.data.attributes.uuid');
  const assessment = workerAssessmentUuid ? await getAssessment(workerAssessmentUuid, assessmentWorkerUuid) : undefined;
  const clonedWorkerAssessment = _.cloneDeep(workerAssessment);
  clonedWorkerAssessment.relationships.assessment.data = assessment;
  return clonedWorkerAssessment;
}
